import React, { useState, useEffect } from 'react';

import { FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import DonateRegistrationModal from '../../../components/DonateRegistrationModal';
import ContractorAuthenticationModal from '../../../components/family-plaza/ContractorAuthenticationModal';
import PortraitPhotoRegistrationModal from '../../../components/family-plaza/PortraitPhotoRegistrationModal';
import { checkModalAuthenticationStatus, requireAuthentication } from '../../../utils/authUtils';
import { getDeceasedPersonDate, getDeceasedPersonProfileText } from '../../../utils/profilePhotoUtils';
import CyberMemorialService from '../../../services/FamilyPlaza/CyberMemorialService';
import FamilyPlazaTopSection from '../../../components/family-plaza/FamilyPlazaTopSection';
import FamilyPlazaSubHeading from '../../../components/family-plaza/FamilyPlazaSubHeading';
import DeceasedPersonProfile from '../../../components/family-plaza/DeceasedPersonProfile';


const DonateMobile = () => {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [donations, setDonations] = useState([]);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('/donate/layer-1910.png');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pagination states
  const [displayedDonations, setDisplayedDonations] = useState([]);
  const [hasMoreDonations, setHasMoreDonations] = useState(false);
  const [donationsPerPage] = useState(5);
  const [allDonations, setAllDonations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);




  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const openModal = () => {
    // Check if user has already completed the authentication modal
    if (checkModalAuthenticationStatus()) {
      // If already authenticated, open donation modal directly
      setIsModalOpen(true);
    } else {
      // If not authenticated, show authentication modal
      setAuthAction('donate');
      setShowAuthModal(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    // Reset authentication state when modal is closed
    setIsAuthenticated(false);
  };

  // Handle donation removal
  const handleDonationRemove = (donationId) => {
    // Show confirmation alert
    const isConfirmed = window.confirm("정말 삭제하시겠습니까?");
    
    if (isConfirmed) {
      console.log('🗑️ Attempting to delete donation with ID:', donationId);
      console.log('📋 Current donations:', donations);
      
      // Find the specific donation to delete
      const donationToDelete = donations.find(donation => donation.id === donationId);
      if (!donationToDelete) {
        console.error('❌ Donation not found for deletion:', donationId);
        alert('삭제할 항목을 찾을 수 없습니다.');
        return;
      }
      
      // Filter out the specific donation
      const newDonations = donations.filter(donation => donation.id !== donationId);
      console.log('✅ Donations after deletion:', newDonations);
      
      // Update localStorage first
      const selectedPersonData = localStorage.getItem('selectedPersonData');
      let customerName = 'default';
      
      if (selectedPersonData) {
        try {
          const personData = JSON.parse(selectedPersonData);
          customerName = personData.customerName || 'default';
        } catch (error) {
          customerName = 'default';
        }
      }
      
      const donationsKey = `donations_${customerName}`;
      localStorage.setItem(donationsKey, JSON.stringify(newDonations));
      
      // Update state after localStorage
      setDonations(newDonations);
      
      // Show success alert
      alert("삭제되었습니다.");
    }
  };

  const handleNavigation = (route) => {
    navigate(route);
  };

  const handleMemorialPost = () => {
    // Check authentication before navigation
    if (!requireAuthentication()) {
      return; // requireAuthentication will handle the alert and redirect
    }
    navigate('/cyber-memorial-search');
  };

  const handleLetterToHeaven = () => {
    // Check authentication before navigation
    if (!requireAuthentication()) {
      return; // requireAuthentication will handle the alert and redirect
    }
    navigate('/letter-to-heaven-board-list');
  };

  const handleManagementInquiry = () => {
    // Check authentication before navigation
    if (!requireAuthentication()) {
      return; // requireAuthentication will handle the alert and redirect
    }
    navigate('/management-search');
  };

  const handleAncestralTablet = () => {
    // Check authentication before navigation
    if (!requireAuthentication()) {
      return; // requireAuthentication will handle the alert and redirect
    }
    navigate('/ancestral-tablet');
  };

  // State to track what action triggered authentication
  const [authAction, setAuthAction] = useState('');

  // Photo icon click handler

  const handlePhotoIconClick = () => {
    const isAuthenticated = checkModalAuthenticationStatus();
    
    if (isAuthenticated) {
      // If already authenticated, open photo modal directly
      setAuthAction('photo');
      setShowPhotoModal(true);
    } else {
      // If not authenticated, open auth modal first
      setShowAuthModal(true);
    }
  };

  // Authentication modal handlers
  const handleAuthConfirm = () => {
    setShowAuthModal(false);
    
    // Based on what triggered the authentication, open the appropriate modal
    if (authAction === 'photo') {
      setShowPhotoModal(true);
    } else {
      setIsModalOpen(true);
    }
    
    // Reset auth action
    setAuthAction('');
  };

  const handleAuthClose = () => {
    setShowAuthModal(false);
  };

  // Photo registration modal handlers
  const handlePhotoConfirm = () => {
    setShowPhotoModal(false);
    navigate(-1); 
  };

  const handlePhotoClose = () => {
    setShowPhotoModal(false);
  };

  // Handle load more donations (client-side pagination)
  const handleLoadMore = () => {
    if (isLoading || currentPage >= totalPages) return;
    const nextPage = currentPage + 1;
    const startIndex = (nextPage - 1) * donationsPerPage;
    const endIndex = startIndex + donationsPerPage;
    const nextBatch = allDonations.slice(startIndex, endIndex);
    if (nextBatch.length > 0) {
      setDisplayedDonations(prev => [...prev, ...nextBatch]);
      setCurrentPage(nextPage);
      setHasMoreDonations(endIndex < allDonations.length);
    }
  };

  // Update displayed donations when donations change
  useEffect(() => {
    if (donations.length === 0) {
      setDisplayedDonations([]);
      setHasMoreDonations(false);
      return;
    }
    
    // If this is the initial load or donations were reset, show first page
    if (displayedDonations.length === 0 && donations.length > 0) {
      const initialDonations = donations.slice(0, donationsPerPage);
      setDisplayedDonations(initialDonations);
    } else if (donations.length < displayedDonations.length) {
      // If donations were deleted, recalculate based on current donations
      const maxDisplayed = Math.min(displayedDonations.length, donations.length);
      const newDisplayedDonations = donations.slice(0, maxDisplayed);
      setDisplayedDonations(newDisplayedDonations);
    } else if (donations.length > displayedDonations.length && displayedDonations.length < donationsPerPage) {
      // If new donations were added and we haven't reached the page limit, show them
      const newDisplayedDonations = donations.slice(0, donationsPerPage);
      setDisplayedDonations(newDisplayedDonations);
    }
    
    // Show button if there are more than 8 donations
    const hasMore = donations.length > donationsPerPage;
    setHasMoreDonations(hasMore);
  }, [donations, donationsPerPage]);

  // Load donations from API (mirror PC)
  useEffect(() => {
    if (!requireAuthentication()) {
      return;
    }

    setIsAuthenticated(false);

    const selectedPersonData = localStorage.getItem('selectedPersonData');
    if (selectedPersonData) {
      try {
        const personData = JSON.parse(selectedPersonData);
        updateMobileProfileData(personData);
      } catch (error) {
        updateMobileProfileData({
          goinName: '故 홍길동',
          enshrineDay: '2011-01-01',
          customerName: '김추모'
        });
      }
    }

 

    const loadDonations = async () => {
      try {
        setIsLoading(true);
        const selected = localStorage.getItem('selectedPersonData');
        if (!selected) {
          setAllDonations([]);
          setDisplayedDonations([]);
          setHasMoreDonations(false);
          setCurrentPage(0);
          setTotalPages(0);
          return;
        }
        const person = JSON.parse(selected);
        const goinId = person.goinId;
        if (!goinId) {
          setAllDonations([]);
          setDisplayedDonations([]);
          setHasMoreDonations(false);
          setCurrentPage(0);
          setTotalPages(0);
          return;
        }
        const response = await CyberMemorialService.getLayflowersByGoinId(goinId, 1, 200);
        if (response && response.layflowers) {
          const sorted = [...response.layflowers].sort((a, b) => new Date(b.registTime) - new Date(a.registTime));
          setAllDonations(sorted);
          const first = sorted.slice(0, donationsPerPage);
          setDisplayedDonations(first);
          setCurrentPage(1);
          setTotalPages(Math.ceil(sorted.length / donationsPerPage));
          setHasMoreDonations(sorted.length > donationsPerPage);
        } else {
          setAllDonations([]);
          setDisplayedDonations([]);
          setHasMoreDonations(false);
          setCurrentPage(0);
          setTotalPages(0);
        }
      } catch (e) {
        setAllDonations([]);
        setDisplayedDonations([]);
        setHasMoreDonations(false);
        setCurrentPage(0);
        setTotalPages(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadDonations();

    // Listen for profile photo updates
    const handleProfilePhotoUpdate = (event) => {
      // Only update if this is for the current user
      const currentPersonData = localStorage.getItem('selectedPersonData');
      let currentCustomerName = 'default';
      
      if (currentPersonData) {
        try {
          const personData = JSON.parse(currentPersonData);
          currentCustomerName = personData.customerName || 'default';
        } catch (error) {
          currentCustomerName = 'default';
        }
      }
      
      // Only update if the event is for the current user
      if (event.detail.customerName === currentCustomerName) {
        updateMobileProfilePhoto(event.detail.photoUrl);
      }
    };

    window.addEventListener('profilePhotoUpdated', handleProfilePhotoUpdate);

    // Listen for donation updates -> reload from API
    const handleDonationUpdate = (event) => {
      loadDonations();
    };

    window.addEventListener('donationUpdated', handleDonationUpdate);

    return () => {
      window.removeEventListener('profilePhotoUpdated', handleProfilePhotoUpdate);
      window.removeEventListener('donationUpdated', handleDonationUpdate);
    };
  }, []);

  const updateMobileProfilePhoto = (photoUrl) => {
    // For mobile, replace the group-129 image
    const mobileGroupImage = document.querySelector('.donate-mobile-group-image-unique img[src*="group-129"]');
    if (mobileGroupImage) {
      mobileGroupImage.src = photoUrl;
    } else {
      // Try alternative selector if the first one doesn't work
      const allMobileGroupImages = document.querySelectorAll('.donate-mobile-group-image-unique img');
      allMobileGroupImages.forEach(img => {
        if (img.src.includes('group-129')) {
          img.src = photoUrl;
        }
      });
    }
    
    // Also try to update any image in the group container
    const groupContainer = document.querySelector('.donate-mobile-group-image-unique');
    if (groupContainer) {
      const img = groupContainer.querySelector('img');
      if (img) {
        img.src = photoUrl;
      }
    }
  };

  const updateMobileProfileData = (personData) => {
    // Update mobile profile text content
    const profileTexts = document.querySelectorAll('.donate-mobile-profile-text-unique p');
    if (profileTexts.length >= 3) {
      profileTexts[0].textContent = `${personData.enshrineDay} 별세하신`;
      profileTexts[1].textContent = personData.goinName;
      profileTexts[2].textContent = '사이버 추모관 입니다.';
    }
    
    // Reset profile photo to default when switching users
    const groupContainer = document.querySelector('.donate-mobile-group-image-unique');
    if (groupContainer) {
      const img = groupContainer.querySelector('img');
      if (img) {
        img.src = '/mobile donate/group-129.png';
      }
    }
  };

  const handleDonationSubmit = (newDonation) => {
    // Get current customer name
    const selectedPersonData = localStorage.getItem('selectedPersonData');
    let customerName = 'default';
    
    if (selectedPersonData) {
      try {
        const personData = JSON.parse(selectedPersonData);
        customerName = personData.customerName || 'default';
      } catch (error) {
        customerName = 'default';
      }
    }
    
    // Load current donations from localStorage
    const donationsKey = `donations_${customerName}`;
    const savedDonations = localStorage.getItem(donationsKey);
    let currentDonations = [];
    
    if (savedDonations) {
      try {
        currentDonations = JSON.parse(savedDonations);
      } catch (error) {
        console.error('Error parsing existing donations:', error);
        currentDonations = [];
      }
    }
    
    // Add new donation to the beginning (latest first)
    currentDonations.unshift(newDonation);
    
    // Save back to localStorage
    localStorage.setItem(donationsKey, JSON.stringify(currentDonations));
    
    // Update state immediately
    setDonations(currentDonations);
    
    // Dispatch event to notify other components (PC) about donation update
    window.dispatchEvent(new CustomEvent('donationUpdated', {
      detail: {
        customerName: customerName,
        donationData: newDonation
      }
    }));
    
    // Close modal
    closeModal();
  };

  return (
    <div className="donate-mobile-unique announcement-list-page donate-info-page-pc">
   
   <FamilyPlazaTopSection />
 
   <main className="main-content">

   <FamilyPlazaSubHeading heading="사이버추모관" />

      <DeceasedPersonProfile 
        handlePhotoIconClick={handlePhotoIconClick}
      />


      {/* Navigation Tabs */}
      <div className="donate-mobile-nav-tabs-unique">
        <button 
          className="donate-mobile-nav-tab-unique donate-mobile-nav-tab-active-unique"
          onClick={() => navigate('/donate')}
        >
          <span>헌화하기</span>
        </button>
        <button 
          className="donate-mobile-nav-tab-unique"
          onClick={() => navigate('/memorial-post-board-list')}
        >
          <span>추모글</span>
        </button>
        <button 
          className="donate-mobile-nav-tab-unique"
          onClick={() => navigate('/memorial-album-board-list')}
        >
          <span>추모앨범</span>
        </button>
      </div>

      {/* Donate Button */}
      <div className="donate-mobile-button-section-unique">
        <button 
          className="donate-mobile-donate-button-unique"
          onClick={openModal}
        >
          헌화하기
        </button>
      </div>

      {/* Content Cards */}
      <div className="donate-mobile-content-cards-unique">
        {/* Show user's donations first (latest first) */}
        {displayedDonations.map((donation) => (
          <div key={donation.layflowerId || donation.id} className="donate-mobile-content-card-unique">
            <div className="donate-mobile-card-image-unique">
              <img src={donation.flowerImageUrl || donation.flowerImage} alt="Flowers" />
            </div>
            <div className="donate-mobile-card-content-unique">
              <div className="donate-mobile-card-decorator-unique"></div>
              <div className="donate-mobile-card-message-unique">
                <span style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.2em',
                  maxHeight: '2.4em'
                }}>
                  {donation.layflowerTitle || donation.layflowerContents || donation.message}
                </span>
              </div>
              <div className="donate-mobile-card-sender-unique">
                <span style={{ marginLeft: 8 }}>
                  {donation.registTime ? new Date(donation.registTime).toLocaleDateString('ko-KR') : donation.date}
                </span>
              </div>
            </div>
            <div 
              className="donate-mobile-card-close-unique"
              onClick={() => handleDonationRemove(donation.layflowerId || donation.id)}
            >
              <FiX />
            </div>
          </div>
        ))}
        {hasMoreDonations && (
          <button 
            className="donate-mobile-load-more-button-unique" 
            onClick={handleLoadMore}
            disabled={isLoading}
          >
            {isLoading ? '로딩 중...' : '더보기'}
          </button>
        )}
      </div>


      {/* Donate Registration Modal */}
      <DonateRegistrationModal 
        isOpen={isModalOpen} 
        onClose={closeModal}
        onSubmit={handleDonationSubmit}
      />

      {/* Authentication Modal */}
      {showAuthModal && (
        <ContractorAuthenticationModal
          isOpen={showAuthModal}
          onConfirm={handleAuthConfirm}
          onClose={handleAuthClose}
        />
      )}

      {/* Photo Registration Modal */}
      {showPhotoModal && (
        <PortraitPhotoRegistrationModal
          isOpen={showPhotoModal}
          onConfirm={handlePhotoConfirm}
          onClose={handlePhotoClose}
        />
      )}
 </main>

    </div>
  );
};

export default DonateMobile; 